import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	baseSettingsColor,
	baseSettingsLogin,
	baseSettingsHeader,
} from '../../slices/thunks';
import { darkenColor, hexToRgb, hexToRgba } from './useColorArrayHooks';

// Cache para evitar requests duplicadas
const settingsCache = {
	isLoaded: false,
	isLoading: false,
};

const useAppSettings = () => {
	const dispatch = useDispatch();
	const hasInitialized = useRef(false);

	const selectorSettings = createSelector(
		state => state.Settings,
		result => ({
			itemSettingsColor: result.itemSettingsColor,
			itemSettingsLogin: result.itemSettingsLogin,
			itemSettingsHeader: result.itemSettingsHeader,
			isSettingsLoading: result.isSettingsLoading,
			isSettingsLogin: result.isSettingsLogin,
			isSettingsHeader: result.isSettingsHeader,
			urlToRedirect: result.urlToRedirect,
			isSettingsSuccess: result.isSettingsSuccess,
		})
	);

	const {
		itemSettingsColor,
		itemSettingsLogin,
		itemSettingsHeader,
		isSettingsLoading,
		isSettingsLogin,
		isSettingsHeader,
		urlToRedirect,
		isSettingsSuccess,
	} = useSelector(selectorSettings);

	// Función única para cargar todas las configuraciones
	const loadAllSettings = async () => {
		if (settingsCache.isLoading || settingsCache.isLoaded) {
			return;
		}

		settingsCache.isLoading = true;

		try {
			// Ejecutar todas las requests en paralelo de forma controlada
			await Promise.all([
				dispatch(baseSettingsColor()),
				dispatch(baseSettingsLogin()),
				dispatch(baseSettingsHeader()),
			]);

			settingsCache.isLoaded = true;
		} catch (error) {
			console.error('Error loading app settings:', error);
		} finally {
			settingsCache.isLoading = false;
		}
	};

	// Inicializar settings solo una vez
	useEffect(() => {
		if (!hasInitialized.current) {
			hasInitialized.current = true;
			loadAllSettings();
		}
	}, []);

	// Aplicar colores CSS cuando estén disponibles
	useEffect(() => {
		if (isSettingsSuccess && itemSettingsColor) {
			const vz_primary_rgb = hexToRgb(itemSettingsColor.primary_color);
			const vz_primary_rgb_border = darkenColor(
				itemSettingsColor.header_color,
				0.02
			);
			const vz_secondary_rgb = hexToRgba(itemSettingsColor.secondary_color);
			const vz_header_rgb = hexToRgb(itemSettingsColor.header_color);
			const vz_header_bg = hexToRgba(itemSettingsColor.header_color, 0.95);
			const vz_header_border = darkenColor(
				itemSettingsColor.header_color,
				0.02
			);
			const vz_topbar_user_bg = darkenColor(
				itemSettingsColor.header_color,
				0.1
			);
			const vz_header_item_sub_color = darkenColor('#FFFFFF', 0.1);
			const vz_header_item_bg = darkenColor(
				itemSettingsColor.header_color,
				0.15
			);
			const vz_header_item_color = darkenColor('#FFFFFF', 0.1);
			const vz_topbar_search_bg = darkenColor('#FFFFFF', 0.01);
			const vz_primary_text_emphasis = hexToRgba(
				itemSettingsColor.primary_color,
				0.7
			);

			// Aplicar variables CSS
			const docElement = document.documentElement;
			docElement.style.setProperty(
				'--vz-primary',
				itemSettingsColor.primary_color
			);
			docElement.style.setProperty(
				'--vz-primary-border',
				vz_primary_rgb_border
			);
			docElement.style.setProperty('--vz-primary-rgb', vz_primary_rgb);
			docElement.style.setProperty(
				'--vz-secondary',
				itemSettingsColor.secondary_color
			);
			docElement.style.setProperty('--vz-header-bg', vz_header_bg);
			docElement.style.setProperty('--vz-header-border', vz_header_border);
			docElement.style.setProperty('--vz-topbar-user-bg', vz_topbar_user_bg);
			docElement.style.setProperty(
				'--vz-header-item-sub-color',
				vz_header_item_sub_color
			);
			docElement.style.setProperty('--vz-header-item-bg', vz_header_item_bg);
			docElement.style.setProperty(
				'--vz-header-item-color',
				vz_header_item_color
			);
			docElement.style.setProperty(
				'--vz-topbar-search-bg',
				vz_topbar_search_bg
			);
			docElement.style.setProperty(
				'--vz-primary-text-emphasis',
				vz_primary_text_emphasis
			);
			docElement.style.setProperty(
				'--vz-topnav-item-active-color',
				vz_primary_text_emphasis
			);
			docElement.style.setProperty(
				'--vz-link-hover-color',
				itemSettingsColor.secondary_color
			);
		}
	}, [itemSettingsColor, isSettingsSuccess]);

	// Función para obtener logos específicos
	const getLogo = logoType => {
		if (logoType === 'login') {
			return {
				logoSrc: itemSettingsLogin?.login_logo || '',
				isLoading: isSettingsLogin,
				urlToRedirect,
			};
		} else if (logoType === 'header') {
			return {
				logoSrc: itemSettingsHeader?.header_logo || '',
				isLoading: isSettingsHeader,
				urlToRedirect,
			};
		}
		return { logoSrc: '', isLoading: false, urlToRedirect };
	};

	return {
		// Settings data
		itemSettingsColor,
		itemSettingsLogin,
		itemSettingsHeader,

		// Loading states
		isSettingsLoading,
		isSettingsLogin,
		isSettingsHeader,

		// URL redirect
		urlToRedirect,

		// Helper functions
		getLogo,

		// Manual reload if needed
		reloadSettings: loadAllSettings,
	};
};

export default useAppSettings;
